import React, { useEffect, useMemo } from 'react';
import { Box, Center, Heading, Text, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';

import { VPPProgramOffer } from 'clipsal-cortex-types/src/api/api-saturn-vpp';
import { CenteredLoader } from 'clipsal-cortex-ui/src/components/CenteredLoader';

import { ArcButton } from '../../../common/components/ArcButton';
import { ContactSupportButton } from '../../../common/components/ContactSupportButton';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { useParamBackUrl } from '../../../common/hooks/use-param-back-url';
import { MANUFACTURER_ID_SATURN } from '../../devices/devices-helper';
import { selectInverters } from '../../site/siteSlice';
import { CustomVppIcon } from './CustomVPPIcon';
import { getProgramOfferByStatus } from './helpers';
import { useGetVppProgramOffers } from './vppApi';
import { VPPHelpGuide } from './VPPHelpGuide';

export function VPPHome() {
  const saturnInverter = useSelector(selectInverters).find((device) =>
    [MANUFACTURER_ID_SATURN].includes(device.manufacturer_id)
  );
  const { t } = useTranslation();
  const backURL = useParamBackUrl('../../../account/dashboard?direction=back');
  const toast = useToast({ duration: 3000, isClosable: true });

  const {
    isLoading: isProgramOffersLoading,
    isFetching: isProgramOffersFetching,
    isError: isProgramOffersError,
  } = useGetVppProgramOffers(saturnInverter!.id, !saturnInverter?.id);
  const isLoaded = !isProgramOffersLoading && !isProgramOffersFetching;
  const isError = isProgramOffersError;

  useEffect(() => {
    if (isLoaded && isError) {
      toast.closeAll();
      toast({
        title: t('VPP.error getting vpp data'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
      });
    }
  }, [isLoaded, isError, toast, t]);

  return (
    <SlidingAnimationPageBase backURL={backURL} title={t('VPP.title')}>
      <Box mx={4} mb={4}>
        {!!saturnInverter?.id && <VPPWizardRedirect isParentLoaded={isLoaded} inverterId={saturnInverter.id} />}
      </Box>
    </SlidingAnimationPageBase>
  );
}

/*
The following component is responsible for facilitating the user's journey through VPP participation.
The user's journey is as follows:
1️⃣ To be eligible to join a program, the user must provide their utility provider and utility account number.
2️⃣ The user will receive a notification from Saturn Cloud when they are eligible to join a VPP program.
3️⃣ The user accepts the offer in SE-Monitor. Their enrolment is pending and get a notification if approved/denied.
4️⃣ Once the user's inverter is enrolled in a VPP program, they can view current & upcoming VPP events and opt out.
@returns The redirect/UI component, to be returned by the caller component as JSX.
*/
function VPPWizardRedirect({ isParentLoaded, inverterId }: { isParentLoaded: boolean; inverterId: number }) {
  const { t } = useTranslation();
  const { programOffers } = useGetVppProgramOffers(inverterId);

  return useMemo(() => {
    if (!isParentLoaded) return <CenteredLoader text={`${t('Common.loading')}...`} isFullHeight />;

    if (!programOffers.length) {
      return <PreEnrolmentForm />;
    }

    // The current scope for VPP is limited to one program per inverter, here we are assuming the user --
    // -- will only ever be enrolled in one program.
    const enrolledProgram = getProgramOfferByStatus(programOffers, 'Enrolled');
    if (enrolledProgram) return <Navigate to={`../active-program`} />;

    const eligibleProgram = getProgramOfferByStatus(programOffers, 'Eligible');
    if (eligibleProgram) return <Navigate to={`../join-program`} />;

    const pendingProgram = getProgramOfferByStatus(programOffers, 'Pending');
    if (pendingProgram) return <PendingEnrolment programOffer={pendingProgram} />;

    const deniedProgram = getProgramOfferByStatus(programOffers, 'Ineligible');
    if (deniedProgram) return <EnrolmentDenied />;

    return <PreEnrolmentForm />;
  }, [isParentLoaded, programOffers, t]);
}

const PreEnrolmentForm = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <VPPHelpGuide>
      <ArcButton
        arcColor="#3DCD58"
        minWidth="80%"
        mb={4}
        py={6}
        onClick={() => navigate('../enrol?direction=forward')}
        data-testid="continue-button"
      >
        {t('Common.get started')}
      </ArcButton>
    </VPPHelpGuide>
  );
};

const EnrolmentDenied = () => {
  const { t } = useTranslation();
  return (
    <Box data-testid="vpp-enrolment-denied-screen">
      <CustomVppIcon iconColor={'#FF5B44'} />
      <Heading my={2} textAlign="center">
        {t('VPP.enrolment denied title')}
      </Heading>
      <Text textAlign="center" mb={4}>
        {t('VPP.enrolment denied description')}
      </Text>
      <Center>
        <ContactSupportButton path="../../../account/support?direction=back" />
      </Center>
    </Box>
  );
};

function PendingEnrolment({ programOffer }: { programOffer: VPPProgramOffer }) {
  const { t } = useTranslation();
  return (
    <Box data-testid="vpp-enrolment-pending-screen">
      <CustomVppIcon iconColor={'orange'} />
      <Heading my={2} textAlign="center">
        {t('VPP.enrolment pending title')}
      </Heading>

      <Text mb={4} textAlign="center" data-testid="vpp-enrolment-pending-description">
        {t('VPP.enrolment pending description 1', {
          utilityName: programOffer.program.utility.name,
          programName: programOffer.program.name,
        })}
      </Text>
      <Text mb={4} textAlign="center">
        {t('VPP.enrolment pending description 2')}
      </Text>
      <Center>
        <ContactSupportButton path="../../../account/support?direction=back" />
      </Center>
    </Box>
  );
}
